import React, { useCallback, useContext, useEffect, useState } from 'react'
import { isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useApiErrorMessage } from '../../../i18n/useApiErrorMessage'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
// import { useHasRoles } from "react-simple-rbac";

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'

import dayjs from '../../../utils/dayjsConfig'

import Layout from '../../Layout/Layout'
import CustomBreadcrumbs from '../../CustomBreadcrumbs/CustomBreadcrumbs'
import InformationModal from '../../Modals/ConfirmationModal/InformationModal'

import {
  deletePeriod,
  editPeriod,
  getPeriodById,
} from '../../../services/ParametersService'

import { computeBackendDayLimit } from '../../../utils/quincena'

import { ParametroContext } from '../../../context/parametro/ParametroContext'

const periodsBefore = [
  {
    id: 1,
    value: '1',
  },
  {
    id: 2,
    value: '2',
  },
  {
    id: 3,
    value: '3',
  },
  {
    id: 4,
    value: '4',
  },
  {
    id: 5,
    value: '5',
  },
]

type ParametroPeriodoDetailsType = {
  params: any
}

const CompanyParametroPeriodoDetails: React.FC<ParametroPeriodoDetailsType> = ({
  params,
}) => {
  const { t } = useTranslation('parametrosDetails')
  const getApiErrorMessage = useApiErrorMessage()

  const links: any[] = [
    { id: '1', value: t('breadcrumbLinks.parametros'), isLink: true, to: '/parametrosportal' },
    { id: '2', value: t('breadcrumbLinks.periodType'), isLink: false },
  ]

  const durationArray = [
    {
      id: 0,
      value: t('periodo.durationOptions.monthly'),
    },
    {
      id: 1,
      value: t('periodo.durationOptions.biweekly'),
    },
  ]

  const [previusState, setPreviusState] = useState({
    name: '',
    duration: 1,
    day_limit: '',
    periods_before: '',
  })
  const [openInformationModal, setOpenInformationModal] = useState<boolean>(false)
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [clickEliminar, setClickEliminar] = useState<boolean>(false)
  const [clickGuardar, setClickGuardar] = useState<boolean>(false)
  const [clickCancelar, setClickCancelar] = useState<boolean>(false)
  const [modalTitle, setModalTitle] = useState<string>('')
  const [modalMessage, setModalMessage] = useState<string>('')
  const [disabledOnSubmit, setDisabledOnSubmit] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [fetchError, setFetchError] = useState<boolean>(false)
  const [fetchErrorMessage, setFetchErrorMessage] = useState<string>('')
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)

  const navigate = useNavigate()

  const { getAccessTokenSilently } = useAuth0()

  const { setTabValue } = useContext(ParametroContext)

  const { periodoId } = params

  // const isNotReadOnly = useHasRoles(["admin", "basic", "manager"], true);
  const isNotReadOnly = true

  const now = dayjs().tz('America/Argentina/Buenos_Aires')
  const todayIsFirstHalf = now.date() <= 15

  const [searchParams] = useSearchParams()
  const filterYear = Number(searchParams.get('year'))
  const filterMonth = Number(searchParams.get('month'))
  const defaultCalendarMonth =
    filterYear > 0 && filterMonth >= 1 && filterMonth <= 12
      ? dayjs().year(filterYear).month(filterMonth - 1).startOf('month')
      : undefined

  const formik = useFormik({
    initialValues: {
      name: '',
      duration: 1,
      day_limit: '',
      periods_before: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().max(55).required(t('periodo.validation.nameRequired')),
      duration: Yup.number().max(365).required(t('periodo.validation.durationRequired')),
      day_limit: Yup.string()
        .test(
          'is-date',
          t('periodo.validation.invalidDate'),
          value => !!value && dayjs(value).isValid(),
        )
        .required(t('periodo.validation.dayLimitDateRequired')),
    }),
    onSubmit: async data => {
      setDisabledOnSubmit(true)
      let computedDayLimit: number | null = null
      if (data.day_limit) {
        computedDayLimit = computeBackendDayLimit(Number(data.duration), data.day_limit)
      }
      const finalData = {
        ...data,
        day_limit: computedDayLimit,
        periods_before: Number(data.periods_before),
      }
      const token = await getAccessTokenSilently()
      const result = await editPeriod(token, finalData, periodoId)
      if (result.status === 201 || result.status === 200) {
        handleCloseModal()
        navigate(-1)
      }
    },
  })

  const deletePeriodByID = async () => {
    setDisabledOnSubmit(true)
    const token = await getAccessTokenSilently()
    const result = await deletePeriod(token, periodoId)
    if (result.status === 201 || result.status === 200) {
      navigate(-1)
      handleCloseModal()
    }
  }

  const handleClickGuardar = () => {
    setIsDirty(false)
    setClickCancelar(false)
    setClickGuardar(true)
    setModalTitle(t('periodo.modals.saveTitle'))
    setModalMessage(t('periodo.modals.saveMessage'))
    handleOpenModal()
  }

  const handleClickDirtyEliminar = () => {
    setIsDirty(false)
    setClickGuardar(false)
    setClickCancelar(false)
    setClickEliminar(true)
    setModalTitle(t('periodo.modals.deleteTitle'))
    setModalMessage(t('periodo.modals.deleteMessage'))
    handleOpenModal()
  }

  const handleOpenModal = () => {
    setOpenInformationModal(true)
  }

  const handleCloseModal = () => {
    setOpenInformationModal(false)
  }

  const handleDeshacerCancelar = () => {
    formik.resetForm()
    navigate(-1)
  }

  const handleClickDirtyCancelar = () => {
    setIsDirty(true)
    setClickGuardar(false)
    setClickEliminar(false)
    setClickCancelar(true)
    setModalTitle(t('periodo.modals.cancelChangesTitle'))
    setModalMessage(t('periodo.modals.cancelChangesMessage'))
    handleOpenModal()
  }

  const handleSaveButton = () => {
    formik.submitForm()
  }

  const getPeriod = useCallback(async () => {
    setIsLoading(true)
    const token = await getAccessTokenSilently()
    getPeriodById(token, periodoId)
      .then(result => {
        let dayLimitDate = ''
        if (result.data.reference_date && result.data.reference_date !== '0001-01-01T00:00:00Z') {
          dayLimitDate = dayjs(result.data.reference_date).toISOString()
        } else if (result.data.name) {
          const parsed = dayjs(result.data.name, 'DD/MM/YYYY')
          if (parsed.isValid()) {
            dayLimitDate = parsed.toISOString()
          }
        }
        const commonValues = {
          name: result.data.name,
          duration: result.data.duration,
          day_limit: dayLimitDate,
          periods_before: result.data.periods_before.toString(),
        }
        formik.setValues(commonValues)
        setPreviusState(commonValues)
        setFetchError(false)
        setIsLoading(false)
      })
      .catch(e => {
        console.error(e.response.data.error)
        const commonDefaultValues = {
          name: '',
          duration: 0,
          day_limit: '',
          periods_before: '',
        }
        formik.setValues(commonDefaultValues)
        setPreviusState(commonDefaultValues)
        setFetchErrorMessage(getApiErrorMessage(e))
        setFetchError(true)
        setIsLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodoId])

  useEffect(() => {
    getPeriod()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    document.title = `${window._env_.REACT_APP_EMPRESA_NAME} - ${t('periodo.recordTitlePrefix')} ${formik.values.name} - ${t('periodo.pageTitleSuffix')}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.name])

  useEffect(() => {
    setTabValue(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout title={t('periodo.layoutTitle')} noIcon>
      <CustomBreadcrumbs icon="AccessTimeIcon" links={links} />
      <Grid container>
        <Grid bgcolor="white" item xs={8} borderRadius="6px">
          <Box
            sx={{
              borderRadius: '6px',
              display: 'flex',
              backgroundColor: 'white',
              height: '100%',
              width: '100%',
              paddingRight: fetchError ? '0px' : '18px',
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  borderRadius: '6px',
                  display: 'flex',
                  backgroundColor: 'white',
                  height: '100%',
                  width: '100%',
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Box
                    id="create-novelty-form"
                    borderRadius="6px"
                    sx={{
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '302px',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </Box>
              </Box>
            ) : fetchError ? (
              <Box
                sx={{
                  borderRadius: '6px',
                  display: 'flex',
                  backgroundColor: 'white',
                  height: '100%',
                  width: '100%',
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Box
                    id="create-novelty-form"
                    borderRadius="6px"
                    sx={{
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '302px',
                    }}
                  >
                    <h1>{fetchErrorMessage}</h1>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  component="form"
                  autoComplete="off"
                  borderRadius="6px"
                  sx={{ backgroundColor: 'white', height: '100%' }}
                  onSubmit={formik.handleSubmit}
                >
                  <Grid container justifyContent="space-between">
                    <Grid item xs={6} sx={{ pt: '18px', pl: '18px' }}>
                      <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                        <InputLabel sx={{ color: 'black' }} htmlFor="nombre-input">
                          {t('periodo.fields.name')}
                        </InputLabel>
                      </Box>
                      <TextField
                        id="nombre-input"
                        error={Boolean(formik.touched.name && formik.errors.name)}
                        fullWidth
                        helperText={formik.touched.name && formik.errors.name}
                        name="name"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        type="text"
                        value={formik.values.name}
                        variant="filled"
                        placeholder={t('periodo.fields.namePlaceholder')}
                        InputProps={{
                          disableUnderline: true,
                          readOnly: !isNotReadOnly,
                          sx: {
                            borderRadius: '6px',
                            '.MuiInputBase-input': {
                              paddingBottom: '16px',
                              paddingTop: '16px',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ pt: '18px', pl: '18px' }}>
                      <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                        <InputLabel sx={{ color: 'black' }} htmlFor="duration'input">
                          {t('periodo.fields.duration')}
                        </InputLabel>
                      </Box>
                      <TextField
                        id="duration-select"
                        select
                        error={Boolean(formik.touched.duration && formik.errors.duration)}
                        fullWidth
                        helperText={formik.touched.duration && formik.errors.duration}
                        name="duration"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        type="number"
                        value={formik.values.duration}
                        variant="filled"
                        InputProps={{
                          disableUnderline: true,
                          readOnly: !isNotReadOnly,
                          sx: {
                            borderRadius: '6px',
                            '.MuiInputBase-input': {
                              paddingBottom: '16px',
                              paddingTop: '16px',
                              display: 'flex',
                              justifyContent: 'flex-start',
                            },
                          },
                        }}
                      >
                        {durationArray.map(duration => (
                          <MenuItem key={duration.id} value={duration.id}>
                            {duration.value}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Grid container justifyContent="space-between" paddingBottom="26px">
                    <Grid item xs={6} sx={{ py: '10px', pl: '18px' }}>
                      <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                        <InputLabel sx={{ color: 'black' }} htmlFor="day-limit-input">
                          {t('periodo.fields.dayLimitDate')}
                        </InputLabel>
                      </Box>
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        open={openDatePicker}
                        onOpen={() => setOpenDatePicker(true)}
                        onClose={() => setOpenDatePicker(false)}
                        defaultCalendarMonth={defaultCalendarMonth}
                        value={
                          formik.values.day_limit
                            ? dayjs(formik.values.day_limit)
                            : null
                        }
                        minDate={now.startOf('day')}
                        disablePast
                        shouldDisableDate={(date) => {
                          if (formik.values.duration !== 1) return false
                          if (todayIsFirstHalf) return date.date() > 15
                          return date.date() <= 15
                        }}
                        onChange={(newValue) => {
                          if (newValue && dayjs(newValue).isValid()) {
                            formik.setFieldValue(
                              'day_limit',
                              dayjs(newValue).startOf('day').toISOString(),
                            )
                          } else {
                            formik.setFieldValue('day_limit', '')
                          }
                        }}
                        readOnly={!isNotReadOnly}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            id="day-limit-input"
                            name="day_limit"
                            variant="filled"
                            fullWidth
                            error={Boolean(
                              formik.touched.day_limit && formik.errors.day_limit,
                            )}
                            helperText={formik.touched.day_limit && formik.errors.day_limit}
                            onBlur={formik.handleBlur}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              ...params.InputProps,
                              disableUnderline: true,
                              onClick: () => isNotReadOnly && setOpenDatePicker(true),
                              sx: {
                                borderRadius: '6px',
                                cursor: 'pointer',
                                '.MuiInputBase-input': {
                                  paddingBottom: '16px',
                                  paddingTop: '16px',
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ py: '10px', pl: '18px' }}>
                      <Box display="flex" justifyContent="flex-start" marginBottom="4px">
                        <InputLabel
                          sx={{ color: 'black' }}
                          htmlFor="periods-before-select"
                        >
                          {t('periodo.fields.advance')}
                        </InputLabel>
                      </Box>
                      <TextField
                        id="periods-before-select"
                        select
                        error={Boolean(
                          formik.touched.periods_before && formik.errors.periods_before,
                        )}
                        fullWidth
                        helperText={
                          formik.touched.periods_before && formik.errors.periods_before
                        }
                        name="periods_before"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.periods_before}
                        variant="filled"
                        InputProps={{
                          disableUnderline: true,
                          readOnly: !isNotReadOnly,
                          sx: {
                            borderRadius: '6px',
                            '.MuiInputBase-input': {
                              paddingBottom: '16px',
                              paddingTop: '16px',
                              display: 'flex',
                              justifyContent: 'flex-start',
                            },
                          },
                        }}
                      >
                        {periodsBefore.map(period => (
                          <MenuItem key={period.id} value={period.value}>
                            {period.value}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Grid container maxWidth="sm" paddingBottom="32px">
                    <Box sx={{ display: 'flex' }}>
                      {isNotReadOnly && (
                        <Button
                          sx={{ marginLeft: '18px' }}
                          variant="contained"
                          color="primary"
                          onClick={handleClickGuardar}
                          disabled={
                            !formik.dirty ||
                            !formik.values.name ||
                            !formik.values.day_limit ||
                            !(
                              formik.values.duration >= 0 && formik.values.duration <= 1
                            ) ||
                            isEqual(previusState, formik.values)
                          }
                        >
                          {t('actions.save', { ns: 'common' })}
                        </Button>
                      )}

                      {formik.dirty && !isEqual(previusState, formik.values) ? (
                        <Button
                          sx={{ marginLeft: '18px' }}
                          variant="outlined"
                          color="primary"
                          onClick={handleClickDirtyCancelar}
                        >
                          {t('actions.cancel', { ns: 'common' })}
                        </Button>
                      ) : (
                        <Button
                          sx={{ marginLeft: '18px' }}
                          variant="outlined"
                          color="primary"
                          onClick={() => navigate(-1)}
                        >
                          {t('actions.cancel', { ns: 'common' })}
                        </Button>
                      )}
                      <Button
                        sx={{ marginLeft: '18px' }}
                        variant="contained"
                        color="error"
                        onClick={() => handleClickDirtyEliminar()}
                      >
                        {t('actions.delete', { ns: 'common' })}
                      </Button>
                    </Box>
                  </Grid>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {openInformationModal && (
        <InformationModal
          title={modalTitle}
          message={modalMessage}
          openModal={openInformationModal}
          onClose={handleCloseModal}
          saveForm={handleSaveButton}
          isDirty={isDirty}
          clickCancelar={clickCancelar}
          handleDeshacerCancelar={handleDeshacerCancelar}
          guardar={clickGuardar}
          guardarMessage={modalMessage}
          clickEliminar={clickEliminar}
          onEliminar={deletePeriodByID}
          disabledOnSubmit={disabledOnSubmit}
        />
      )}
    </Layout>
  )
}

export default CompanyParametroPeriodoDetails
